import { createRoot, createSignal, type Signal } from "solid-js"
import { parse } from "valibot"
import { StorageKind } from "../../../core/Storage/StorageKind/StorageKind.enum"
import { StoredItemMetaLocalStorageSchema } from "../../../core/Storage/StoredItem/StoredItemMetaLocalStorage.type"
import { addListener, type EventListenerList } from "../../../event/event"
import { createDefaultInputProfile } from "./createDefaultInputProfile"
import type { InputProfile } from "./InputProfile.type"
import { getAllInputProfilesFromLocalStorage, getInputProfileFromLocalStorage, getInputProfileMetaFromLocalStorage, setInputProfileMetaToLocalStorage, setInputProfileToLocalStorage } from "./inputProfileLocalStorage"
import type { InputProfileMeta } from "./InputProfileMeta.type"

export const inputProfileUpdatedEvent: EventListenerList<InputProfile> = [
  (newValue) => {
    setInputProfileToLocalStorage(newValue)
    // Update meta to point to this profile
    const meta = getInputProfileMetaFromLocalStorage() || { selected: undefined }
    if (!meta.selected || meta.selected.kind !== StorageKind.LocalStorage || meta.selected.id !== newValue.id) {
      const newMeta: InputProfileMeta = {
        selected: parse(StoredItemMetaLocalStorageSchema, {
          kind: StorageKind.LocalStorage,
          id: newValue.id,
        }),
      }
      setInputProfileMetaToLocalStorage(newMeta)
    }
  },
]

const signal: Signal<InputProfile> = createRoot(() => {
  // Get or create the selected profile
  let profile: InputProfile | null = null
  
  const meta = getInputProfileMetaFromLocalStorage()
  if (meta?.selected && meta.selected.kind === StorageKind.LocalStorage) {
    profile = getInputProfileFromLocalStorage(meta.selected.id)
  }
  
  // If no profile found, try to get any existing profile
  if (!profile) {
    const allProfiles = getAllInputProfilesFromLocalStorage()
    if (allProfiles.length > 0) {
      profile = allProfiles[0]
    }
  }
  
  // If still no profile, create a default one
  if (!profile) {
    profile = createDefaultInputProfile(1)
    setInputProfileToLocalStorage(profile)
    const newMeta: InputProfileMeta = {
      selected: parse(StoredItemMetaLocalStorageSchema, {
        kind: StorageKind.LocalStorage,
        id: profile.id,
      }),
    }
    setInputProfileMetaToLocalStorage(newMeta)
  }
  
  const [getInputProfile, setInputProfile] = createSignal<InputProfile>(profile)
  
  // Listen for updates
  addListener(inputProfileUpdatedEvent, (newValue) => {
    setInputProfile(newValue)
  })
  
  return [getInputProfile, setInputProfile] as Signal<InputProfile>
})

export const [getInputProfile, setInputProfile] = signal

export default function createInputProfileSignal() {
  return signal
}

